// src/axiosInstance.ts

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';

const baseURL = process.env.REACT_APP_API_URL;

// Create an Axios instance with default settings
const instance: AxiosInstance = axios.create({
  baseURL: baseURL, // Replace with your API base URL
  timeout: 10000, // Timeout in milliseconds
  headers: {
    'Content-Type': 'application/json'
    // Add other default headers here if necessary
  }
});

// Define a global response handler function
const handleResponseStatus = (response: AxiosResponse) => {
  switch (response.status) {
    case 200:
      // OK
      return response.data;
    case 201:
      // Created
      return response.data;
    case 204:
      // No Content
      return null;
    default:
      // Other success statuses
      return response.data;
  }
};

const handleResponseError = (error: AxiosError) => {
  if (error.response) {
    // The request was made and the server responded with a status code outside the range of 2xx
    switch (error.response.status) {
      case 400:
        console.error('Bad Request:', error.response.data);
        return Promise.reject(new Error('Bad Request: ' + error.response.data));

      case 401:
        console.error('Unauthorized:', error.response.data);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        break;
      case 403:
        console.error('Forbidden:', error.response.data);
        break;
      case 404:
        console.error('Not Found:', error.response.data);
        return Promise.reject(new Error('Not Found: ' + error.response.data));
      case 405:
        console.error('Method Not Allowed:', error.response.data);
        return Promise.reject(
          new Error('Method Not Allowed: ' + error.response.data)
        );
      case 500:
        console.error('Internal Server Error:', error.response.data);
        return Promise.reject(
          new Error('Internal Server Error: ' + error.response.data)
        );
      default:
        //@ts-ignore
        console.error('An unexpected error occurred:', error.response.data?.message);
        return Promise.reject(
          //@ts-ignore
          new Error('An unexpected error occurred: ' + error.response.data?.message)
        );
    }
  } else if (error.request) {
    return Promise.reject(new Error('No response received: ' + error.request));
  } else {
    return Promise.reject(new Error('Error: ' + error.message));
  }
  return Promise.reject(error);
};

// Add request interceptor
instance.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data';
    } else {
      config.headers['Content-Type'] = 'application/json';
    }

    // Modify request config before sending the request
    const token = JSON.parse(localStorage.getItem('token'));
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor
instance.interceptors.response.use(
  (response: AxiosResponse) => handleResponseStatus(response),
  (error: AxiosError) => handleResponseError(error)
);

export default instance;
